import React from 'react';

/**
 * Rotary knob — machined-metal dial with a graduated value arc and a gold
 * pointer. The invisible <input type="range"> preserves keyboard, MIDI-mapping
 * and every existing test. 56px cap.
 */
export const Knob = ({ label, value, min, max, step, onChange, testid, format }) => {
  const pct = (value - min) / (max - min);
  const angle = -135 + pct * 270; // -135°..+135°
  const arcLen = 2 * Math.PI * 22 * (270 / 360);
  const filledLen = arcLen * pct;
  const display = format ? format(value) : (Math.abs(value) < 1 ? value.toFixed(2) : value.toFixed(1));
  return (
    <label className="flex flex-col items-center gap-1 group cursor-pointer select-none">
      <div className="relative w-14 h-14">
        {/* Graduated value arc — track + filled */}
        <svg viewBox="0 0 56 56" className="absolute inset-0 pointer-events-none -rotate-90">
          <circle cx="28" cy="28" r="24" fill="none" stroke="rgba(255,176,0,0.12)" strokeWidth="2" strokeDasharray={`${arcLen} 999`} transform="rotate(135 28 28)" strokeLinecap="round" />
          <circle cx="28" cy="28" r="24" fill="none" stroke="#FFB000" strokeWidth="2" strokeDasharray={`${filledLen} 999`} transform="rotate(135 28 28)" strokeLinecap="round" style={{ filter: 'drop-shadow(0 0 3px rgba(255,176,0,0.6))' }} />
          {/* Tick marks every 30° across the 270° range */}
          {Array.from({ length: 10 }, (_, i) => {
            const a = (135 + i * 30) * Math.PI / 180;
            const x1 = 28 + Math.cos(a) * 20;
            const y1 = 28 + Math.sin(a) * 20;
            const x2 = 28 + Math.cos(a) * 17;
            const y2 = 28 + Math.sin(a) * 17;
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(255,176,0,0.35)" strokeWidth="1" />;
          })}
        </svg>
        {/* Machined-metal cap */}
        <div
          className="absolute inset-[8px] rounded-full"
          style={{
            background: 'radial-gradient(circle at 35% 30%, #2a2a2a 0%, #141414 40%, #050505 100%)',
            boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.15), inset 0 -2px 4px rgba(0,0,0,0.8), 0 2px 4px rgba(0,0,0,0.6)',
          }}
        />
        {/* Pointer rotating on the cap */}
        <div
          className="absolute inset-[8px] flex items-start justify-center pt-1 pointer-events-none"
          style={{ transform: `rotate(${angle}deg)`, transition: 'transform 80ms linear' }}
        >
          <div className="w-[3px] h-[9px] rounded-full bg-[#FFB000] shadow-[0_0_5px_rgba(255,176,0,0.9)]" />
        </div>
        {/* Invisible slider capturing all input — preserves keyboard/MIDI/tests */}
        <input
          type="range"
          min={min} max={max} step={step} value={value}
          onChange={e => onChange(parseFloat(e.target.value))}
          className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
          data-testid={testid}
          aria-label={label}
        />
      </div>
      <span className="text-[10px] uppercase tracking-[0.15em] text-[#FFB000] font-mono font-bold tabular-nums">{display}</span>
      <span className="text-[9px] uppercase tracking-widest text-[#A1A1AA] font-bold group-hover:text-[#FFB000] transition-colors">{label}</span>
    </label>
  );
};
